import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  RichTextContent,
  useCollectionBinding,
  useSlotGroup,
  EditableSlotGroup,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { btnOnLight } from '../utils/buttonStyles';
import {
  isPetFriendlyAmenityTitle,
  requestOpenPetPolicyModal,
} from '../utils/petPolicyModal';
import { isFurnishedAmenityTitle, useCortLeadForm } from '../utils/cortLeadForm';
import { PhotoLightbox } from './shared/PhotoLightbox';
import {
  APARTMENT_PHOTO_CATEGORIES,
  COMMUNITY_PHOTO_CATEGORIES,
  diversifyPhotos,
} from '../utils/diversifyPhotos';
import { ctaLinkProps } from '../utils/scheduleTourModal';

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
  [key: string]: unknown;
}

interface GalleryPhoto {
  image?: string | null;
  original_url?: string | null;
  title?: string | null;
  description?: string | null;
  alt_text?: string | null;
  [key: string]: unknown;
}

const INITIAL_VISIBLE = 6;

const apartmentCtaDef: SlotGroupDefinition = {
  label: 'Apartment CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'EXPLORE FLOOR PLANS' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const communityCtaDef: SlotGroupDefinition = {
  label: 'Community CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Label', type: 'text', defaultValue: 'VIEW GALLERY' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

const ctaClassName =
  `inline-flex items-center justify-center min-w-[11rem] px-8 py-3.5 font-body text-sm font-medium tracking-[0.18em] uppercase ${btnOnLight}`;

const GRID_IMAGE_SIZES = '(max-width: 768px) 50vw, 16vw';

function resolveSrc(photo: GalleryPhoto): string {
  return photo.image || photo.original_url || '';
}

function AmenityPhotoGrid({
  photos,
  bindingPath,
  label,
  categories,
}: {
  photos: GalleryPhoto[];
  bindingPath: string;
  label: string;
  categories: typeof APARTMENT_PHOTO_CATEGORIES;
}) {
  const [expanded, setExpanded] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const closeLightbox = useCallback(() => setSelectedIndex(null), []);

  const validPhotos = useMemo(
    () =>
      diversifyPhotos(
        (photos || []).filter((p) => resolveSrc(p)),
        categories,
        resolveSrc,
      ),
    [photos, categories],
  );

  const visible = expanded ? validPhotos : validPhotos.slice(0, INITIAL_VISIBLE);
  const canToggle = validPhotos.length > INITIAL_VISIBLE;

  return (
    <>
      <EditableCollectionBinding bindingPath={bindingPath} label={label}>
        {validPhotos.length === 0 ? (
          <div className="amenitieslist-grid amenitieslist-grid--empty" aria-hidden="true" />
        ) : (
          <div className="amenitieslist-grid-wrap">
            <div className="amenitieslist-grid">
              {visible.map((photo, i) => {
                const src = resolveSrc(photo);
                const alt = photo.alt_text || photo.title || label;
                return (
                  <button
                    key={src || i}
                    type="button"
                    className="amenitieslist-grid__item group"
                    onClick={() => setSelectedIndex(i)}
                    aria-label={`View photo: ${alt}`}
                  >
                    <VibesitesImage
                      url={src}
                      alt={alt}
                      sizes={GRID_IMAGE_SIZES}
                      className="amenitieslist-grid__img"
                    />
                    <span className="amenitieslist-grid__hover" aria-hidden="true" />
                  </button>
                );
              })}
            </div>

            {canToggle && (
              <button
                type="button"
                className="amenitieslist-viewmore"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? 'View less' : 'View more'}
              </button>
            )}
          </div>
        )}
      </EditableCollectionBinding>

      <PhotoLightbox
        images={validPhotos}
        selectedIndex={selectedIndex}
        onClose={closeLightbox}
        onChange={setSelectedIndex}
      />
    </>
  );
}

function AmenityItemsList({
  items,
  bindingPath,
  label,
}: {
  items: Amenity[];
  bindingPath: string;
  label: string;
}) {
  const hasFurnished = items.some((item) => isFurnishedAmenityTitle(item.title));
  const openCortForm = useCortLeadForm(hasFurnished);

  return (
    <EditableCollectionBinding bindingPath={bindingPath} label={label}>
      {items.length === 0 ? (
        <p className="font-body text-sm text-muted-foreground py-2">No amenities listed.</p>
      ) : (
        <ul className="amenitieslist-list">
          {items.map((item, i) => {
            const title = item.title || '';
            const opensPetPolicy = isPetFriendlyAmenityTitle(title);
            const opensCort = !!openCortForm && isFurnishedAmenityTitle(title);
            return (
              <li key={`${title || 'amenity'}-${i}`} className="amenitieslist-list__item">
                {opensCort ? (
                  <button
                    type="button"
                    className="amenitieslist-list__link"
                    onClick={openCortForm}
                    aria-haspopup="dialog"
                  >
                    {title}
                  </button>
                ) : opensPetPolicy ? (
                  <button
                    type="button"
                    className="amenitieslist-list__link"
                    onClick={requestOpenPetPolicyModal}
                    aria-haspopup="dialog"
                  >
                    {title}
                  </button>
                ) : (
                  title
                )}
              </li>
            );
          })}
        </ul>
      )}
    </EditableCollectionBinding>
  );
}

interface AmenityRowProps {
  headingNode: ReactNode;
  items: Amenity[];
  itemsBindingPath: string;
  itemsLabel: string;
  photos: GalleryPhoto[];
  photosBindingPath: string;
  photosLabel: string;
  photoCategories: typeof APARTMENT_PHOTO_CATEGORIES;
  imagePosition: 'left' | 'right';
  footnote?: ReactNode;
  cta: ReactNode;
}

function AmenityRow({
  headingNode,
  items,
  itemsBindingPath,
  itemsLabel,
  photos,
  photosBindingPath,
  photosLabel,
  photoCategories,
  imagePosition,
  footnote,
  cta,
}: AmenityRowProps) {
  const colRef = useScrollReveal<HTMLDivElement>();

  const contentPanel = (
    <div className="amenitieslist-row__content">
      {headingNode}
      <AmenityItemsList items={items} bindingPath={itemsBindingPath} label={itemsLabel} />
      {footnote}
      {cta}
    </div>
  );

  const imagePanel = (
    <div className="amenitieslist-row__media">
      <AmenityPhotoGrid
        photos={photos}
        bindingPath={photosBindingPath}
        label={photosLabel}
        categories={photoCategories}
      />
    </div>
  );

  return (
    <div
      ref={colRef}
      className={`reveal-fade-up amenitieslist-row amenitieslist-row--img-${imagePosition}`}
    >
      {imagePosition === 'left' ? (
        <>
          {imagePanel}
          {contentPanel}
        </>
      ) : (
        <>
          {contentPanel}
          {imagePanel}
        </>
      )}
    </div>
  );
}

/**
 * Amenities page body — matches the 505 Sutton / Dolben layout:
 * Apartment Features (list + CTA left, 3-col square gallery right), then
 * Community Amenities (gallery left, list + CTA right), with View more and lightbox.
 */
export function AmenitiesList() {
  const apartmentCta = useSlotGroup('amenitieslist.apartment_cta', apartmentCtaDef);
  const communityCta = useSlotGroup('amenitieslist.community_cta', communityCtaDef);

  const communityItems = useCollectionBinding<Amenity>('amenitieslist.communityItems', {
    collection: 'amenities',
    filter: { group: 'Community' },
  });

  const apartmentItems = useCollectionBinding<Amenity>('amenitieslist.apartmentItems', {
    collection: 'amenities',
    filter: { group: 'Apartment' },
  });

  const communityPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.communityPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Community' },
    defaultLimit: 24,
  });

  const apartmentPhotos = useCollectionBinding<GalleryPhoto>('amenitieslist.apartmentPhotos', {
    collection: 'photo_gallery',
    filter: { title: 'Apartment' },
    defaultLimit: 24,
  });

  useRevealStyles();

  const hasContent = communityItems.length > 0 || apartmentItems.length > 0;

  return (
    <section className="amenitieslist bg-[var(--vibe-color-background)] py-10 md:py-14">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 xl:px-10">
        {hasContent ? (
          <div className="amenitieslist-stack">
            {apartmentItems.length > 0 && (
              <AmenityRow
                headingNode={
                  <RichTextContent
                    path="amenitieslist.apartmentHeading"
                    label="Apartment Heading"
                    defaultValue="<p>Apartment Features</p>"
                    as="h2"
                    className="amenitieslist-heading"
                  />
                }
                items={apartmentItems}
                itemsBindingPath="amenitieslist.apartmentItems"
                itemsLabel="Apartment Features"
                photos={apartmentPhotos}
                photosBindingPath="amenitieslist.apartmentPhotos"
                photosLabel="Apartment Photos"
                photoCategories={APARTMENT_PHOTO_CATEGORIES}
                imagePosition="right"
                footnote={
                  <RichTextContent
                    path="amenitieslist.apartmentFootnote"
                    label="Apartment Footnote"
                    defaultValue="<p>*In select homes</p>"
                    as="div"
                    className="amenitieslist-footnote"
                  />
                }
                cta={
                  <EditableSlotGroup
                    groupPath="amenitieslist.apartment_cta"
                    definition={apartmentCtaDef}
                  >
                    <a
                      {...ctaLinkProps(apartmentCta.url || '/floor-plans', apartmentCta._target)}
                      className={`amenitieslist-cta ${ctaClassName}`}
                    >
                      {apartmentCta.label || 'EXPLORE FLOOR PLANS'}
                    </a>
                  </EditableSlotGroup>
                }
              />
            )}

            {communityItems.length > 0 && (
              <AmenityRow
                headingNode={
                  <RichTextContent
                    path="amenitieslist.communityHeading"
                    label="Community Heading"
                    defaultValue="<p>Community Amenities</p>"
                    as="h2"
                    className="amenitieslist-heading"
                  />
                }
                items={communityItems}
                itemsBindingPath="amenitieslist.communityItems"
                itemsLabel="Community Amenities"
                photos={communityPhotos}
                photosBindingPath="amenitieslist.communityPhotos"
                photosLabel="Community Photos"
                photoCategories={COMMUNITY_PHOTO_CATEGORIES}
                imagePosition="left"
                cta={
                  <EditableSlotGroup
                    groupPath="amenitieslist.community_cta"
                    definition={communityCtaDef}
                  >
                    <a
                      {...ctaLinkProps(communityCta.url || '/gallery', communityCta._target)}
                      className={`amenitieslist-cta ${ctaClassName}`}
                    >
                      {communityCta.label || 'VIEW GALLERY'}
                    </a>
                  </EditableSlotGroup>
                }
              />
            )}
          </div>
        ) : (
          <div className="py-12 text-center text-[var(--vibe-color-text-muted)]">
            <p className="font-body text-lg">Amenities will be displayed here.</p>
          </div>
        )}
      </div>

      <style>{`
        .amenitieslist-stack {
          display: flex;
          flex-direction: column;
          gap: 3.5rem;
        }

        .amenitieslist-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2.5rem 3rem;
          align-items: start;
        }

        @media (max-width: 900px) {
          .amenitieslist-row {
            grid-template-columns: 1fr;
            gap: 1.75rem;
          }
          /* Stack list above photos on mobile/tablet; desktop keeps side-by-side */
          .amenitieslist-row--img-left > .amenitieslist-row__content {
            order: -1;
          }
        }

        .amenitieslist-heading {
          font-family: var(--vibe-font-heading);
          font-size: clamp(1.5rem, 2.5vw, 2.5rem);
          font-weight: 700;
          color: var(--vibe-color-primary);
          text-transform: uppercase;
          letter-spacing: 0.2em;
          line-height: 1.1;
          margin: 0 0 1.25rem;
        }

        .amenitieslist-list {
          list-style: disc;
          padding-left: 1.25rem;
          margin: 0 0 1.5rem;
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 1.5rem;
          row-gap: 0.35rem;
        }

        @media (max-width: 480px) {
          .amenitieslist-list {
            grid-template-columns: 1fr;
          }
        }

        .amenitieslist-list__item {
          font-family: var(--vibe-font-body);
          font-size: 1rem;
          line-height: 1.55;
          color: var(--vibe-color-text);
          padding-left: 0.15rem;
        }

        .amenitieslist-list__link {
          display: inline;
          padding: 0;
          border: 0;
          background: transparent;
          font: inherit;
          text-align: left;
          color: var(--vibe-color-primary);
          text-decoration: underline;
          text-underline-offset: 3px;
          cursor: pointer;
        }

        .amenitieslist-list__link:hover {
          opacity: 0.8;
        }

        .amenitieslist-footnote {
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          font-style: italic;
          color: var(--vibe-color-text-muted, var(--vibe-color-text));
          margin: -0.75rem 0 1.5rem;
        }

        .amenitieslist-footnote p {
          margin: 0;
        }

        .amenitieslist-cta {
          margin-top: 0.25rem;
        }

        .amenitieslist-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 0.5rem;
        }

        @media (max-width: 640px) {
          .amenitieslist-grid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        .amenitieslist-grid__item {
          position: relative;
          display: block;
          width: 100%;
          aspect-ratio: 1;
          overflow: hidden;
          padding: 0;
          border: 0;
          background: transparent;
          cursor: pointer;
        }

        .amenitieslist-grid__img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.55s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .amenitieslist-grid__item:hover .amenitieslist-grid__img,
        .amenitieslist-grid__item:focus-visible .amenitieslist-grid__img {
          transform: scale(1.08);
        }

        .amenitieslist-grid__hover {
          position: absolute;
          inset: 0;
          background: rgba(0, 0, 0, 0.18);
          opacity: 0;
          transition: opacity 0.3s ease;
          pointer-events: none;
        }

        .amenitieslist-grid__item:hover .amenitieslist-grid__hover,
        .amenitieslist-grid__item:focus-visible .amenitieslist-grid__hover {
          opacity: 1;
        }

        .amenitieslist-viewmore {
          display: inline-block;
          margin-top: 0.75rem;
          padding: 0;
          border: 0;
          background: transparent;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-primary);
          text-decoration: underline;
          text-underline-offset: 3px;
          cursor: pointer;
        }

        .amenitieslist-viewmore:hover {
          opacity: 0.8;
        }

        @media (prefers-reduced-motion: reduce) {
          .amenitieslist-grid__img {
            transition: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.apartmentHeading': {
      label: 'Apartment Heading',
      type: 'richtext',
      defaultValue: '<p>Apartment Features</p>',
    },
    'amenitieslist.apartmentFootnote': {
      label: 'Apartment Footnote',
      type: 'richtext',
      defaultValue: '<p>*In select homes</p>',
    },
    'amenitieslist.communityHeading': {
      label: 'Community Heading',
      type: 'richtext',
      defaultValue: '<p>Community Amenities</p>',
    },
  },
  slotGroups: {
    'amenitieslist.apartment_cta': apartmentCtaDef,
    'amenitieslist.community_cta': communityCtaDef,
  },
  collectionBindings: {
    'amenitieslist.apartmentItems': {
      label: 'Apartment Features',
      collection: 'amenities',
      filter: { group: 'Apartment' },
      description:
        'Amenities whose group is exactly "Apartment". Shown as the Apartment Features bullet list.',
    },
    'amenitieslist.communityItems': {
      label: 'Community Amenities',
      collection: 'amenities',
      filter: { group: 'Community' },
      description:
        'Amenities whose group is exactly "Community". Shown as the Community Amenities bullet list.',
    },
    'amenitieslist.apartmentPhotos': {
      label: 'Apartment Photos',
      collection: 'photo_gallery',
      filter: { title: 'Apartment' },
      description:
        'Photo gallery images whose title is exactly "Apartment". Shown in the Apartment Features grid.',
    },
    'amenitieslist.communityPhotos': {
      label: 'Community Photos',
      collection: 'photo_gallery',
      filter: { title: 'Community' },
      description:
        'Photo gallery images whose title is exactly "Community". Shown in the Community Amenities grid.',
    },
  },
};

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default AmenitiesList;
